import { Button, ButtonType, FCX, getRem } from "@datassential/platform-ui-lib";
import { ReactNode, useCallback, useId, useMemo } from "react";
import cn from "classnames";
import { useCommonDataContext } from "@core/CommonDataContext";
import { MdTune } from "react-icons/md";
import { useSelectFilters } from "@vms/SelectFilters/SelectFiltersContext";
import SelectFiltersModal from "@vms/SelectFilters/SelectFiltersModal";
import useModal from "@components/Modal/useModal";
import { IoIosArrowDown } from "react-icons/io";
import Field from "@components/Field";
import { deepEqual } from "@core/utils/object";

export enum SELECT_FILTERS_AS {
    BUTTON="button",
    FIELD="field",
}

const SelectFilters: FCX<{
    label?: ReactNode;
    placeholder?: string;
    as?: SELECT_FILTERS_AS;
    onApply?: () => void;
}> = ({
    testId,
    className,
    label,
    placeholder = "",
    as = SELECT_FILTERS_AS.BUTTON,
    onApply,
}) => {
    const id = useId();
    const { isLoading } = useCommonDataContext();
    const {
        setSearch,
        selectedFilters,
        setSelectedFilters,
        appliedFilters,
        setAppliedFilters
    } = useSelectFilters();
    const {
        isOpen,
        openModal,
        closeModal,
    } = useModal()

    const cancelFilters = useCallback(() => {
        closeModal();
        setSearch("");
        setSelectedFilters(structuredClone(appliedFilters));
    }, [appliedFilters, setSelectedFilters, setSearch, closeModal]);

    const applyFilters = useCallback(() => {
        closeModal();
        setSearch("");
        setAppliedFilters(structuredClone(selectedFilters));
        onApply?.();
    }, [selectedFilters, setAppliedFilters, setSearch, closeModal, onApply]);

    const resetFilters = useCallback(
        () => {
            setSearch("");
            setSelectedFilters({
                macStages: [],
                heatScores: [],
                categories: [],
            });
        },
        [setSearch, setSelectedFilters]
    );

    const isValueChanged = useMemo(
        () => !deepEqual(appliedFilters, selectedFilters),
        [appliedFilters, selectedFilters]
    );
    
    const thumbnail = useMemo(() => {
        if (isLoading) return null;

        let thumbnail;

        switch (as) {
            case SELECT_FILTERS_AS.BUTTON: {
                thumbnail = (
                    <div className="SelectFilters__thumbnail">
                        {label && (
                            <div className="SelectFilters__button-label appears-right del-4">
                                {label}
                            </div>
                        )}
                        <Button
                            type={ButtonType.Button}
                            className={
                                cn(
                                    "SelectFilters__button",
                                    "Indicatable",
                                    className,
                                    {
                                        "is-applied": appliedFilters.macStages.length > 0 || appliedFilters.categories.length > 0,
                                    }
                                )
                            }
                            testId={testId}
                            onClick={openModal}
                            data-tooltip-id={id}
                        >
                            <MdTune style={{
                                width: getRem(22),
                                height: getRem(22),
                            }}/>&nbsp;
                            <span data-testid="textSelectFilters">
                        FILTERS
                    </span>
                        </Button>
                    </div>
                )
                break;
            }
            case SELECT_FILTERS_AS.FIELD: {
                thumbnail = (
                    <Field
                        label={label}
                        modifiers={['min-width']}
                        className={className}
                    >
                        <div
                            onClick={openModal}
                            data-testid="selectThumbnailInput"
                            className={cn(
                                "SelectComponent__thumbnail",
                                "Indicatable",
                                {
                                    "is-applied": appliedFilters.macStages.length > 0 || appliedFilters.categories.length > 0,
                                },
                            )}
                        >
                            <div className="SelectComponent__thumbnail-inner">
                                {placeholder}
                            </div>
                            <IoIosArrowDown className="SelectComponent__thumbnail-icon"/>
                        </div>
                    </Field>
                )
                break;
            }

            default:
                break;
        }

        return thumbnail
    }, [isLoading, as, appliedFilters, placeholder, className, id, label, openModal, testId])

    if (isLoading) {
        return null;
    }

    return (
        <>
            {thumbnail}
            {
                isOpen && (
                    <SelectFiltersModal
                        onClose={closeModal}
                        applyFilters={isValueChanged ? applyFilters : cancelFilters}
                        cancelFilters={cancelFilters}
                        resetFilters={resetFilters}
                        testId="blockSelectFiltersModal"
                    />
                )
            }
        </>
    );
};

export default SelectFilters;